/**
 * DatasetsListPage — table of datasets for the current team.
 *
 * Each row links to DatasetDetailPage. Header has a "New dataset" CTA going
 * to DatasetEditPage in create mode.
 */

import { Database, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTeam } from '@/contexts/TeamContext'
import { useDatasets } from '@/hooks/useDatasets'

function formatRelative(iso: string): string {
  const created = new Date(iso)
  const diffMs = Date.now() - created.getTime()
  const days = Math.floor(diffMs / 86_400_000)
  if (days < 1) return 'Today'
  if (days < 2) return 'Yesterday'
  if (days < 30) return `${days.toString()}d ago`
  return created.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function DatasetsListPage(): React.JSX.Element {
  const { isRestricted } = useTeam()
  const { data: datasets, isLoading, isError, error } = useDatasets()

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Datasets"
        description="Collections of traces used for evaluation and fine-tuning."
        actions={
          // No dataset creation on restricted demo teams (TRA-832)
          isRestricted ? undefined : (
            <Button asChild>
              <Link to="/datasets/new">
                <Plus className="mr-2 h-4 w-4" />
                New dataset
              </Link>
            </Button>
          )
        }
      />

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {isError && (
        <p className="text-sm text-destructive">
          Failed to load datasets: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {!isLoading && !isError && datasets && datasets.length === 0 && (
        <EmptyState
          icon={Database}
          title="No datasets yet"
          description="Create a dataset to start curating traces for evaluation or fine-tuning."
          action={
            isRestricted ? undefined : (
              <Button asChild>
                <Link to="/datasets/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New dataset
                </Link>
              </Button>
            )
          }
        />
      )}

      {!isLoading && !isError && datasets && datasets.length > 0 && (
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Display name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead className="hidden md:table-cell">Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datasets.map((dataset) => (
                <TableRow key={dataset.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/datasets/${dataset.id}`}
                      className="text-foreground hover:underline"
                    >
                      {dataset.display_name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {dataset.slug}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {dataset.item_count}
                  </TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                    {formatRelative(dataset.updated_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </PageContainer>
  )
}
